import {
	Box,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Switch,
} from "@mui/material";
import { SalutationType, useUpdateContactDeleteMutation } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import { BoundTextField, useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useMemo } from "react";

import { ContactEditProps } from "./ContactEditProps";

import type { ContactExtended } from "@repo/server-api/types";

export const PersonIdentification = (
	props: ContactEditProps & { isDialog?: boolean; readOnly?: boolean },
) => {
	const { t } = useTranslation();

	const { showNotification } = useNotification();

	const { contact, setContact, errors, setErrors } = props;

	const useBinding = <TValue,>(selector: (m: ContactExtended) => TValue) => {
		return useBoundDeep(contact, setContact, selector, errors, setErrors);
	};

	const firstNameField = useBinding((c) => c.firstName);
	const lastNameField = useBinding((c) => c.lastName);

	// TODO fix this 2 to useBinding, not useMemo
	const salutationField = useMemo(() => contact.salutation ?? "", [contact.salutation]);
	const titleField = useMemo(() => contact.title ?? "", [contact.title]);

	const contactNumberField = useBinding((c) => c.contactNumber);

	const [deleteContact] = useUpdateContactDeleteMutation();

	const setInContact = (patch: Partial<ContactExtended>): void => {
		if (contact == null) return;
		setContact({ ...contact, ...patch } as ContactExtended);
	};

	return (
		<div>
			{props.isDialog !== true ? (
				<Box className="fullCenter">
					<FormControlLabel
						control={
							<Switch
								checked={!contact.isDeleted}
								onChange={(event) => {
									const v = !event.target.checked;
									void deleteContact({
										variables: { id: contact.id, isDeleted: v },
									}).then(() => {
										if (v) showNotification(t("message.contact-inactivated"), "success");
										else showNotification(t("message.contact-restored"), "success");
										setInContact({ isDeleted: v });
									});
								}}
							/>
						}
						label={t("Active")}
					/>
				</Box>
			) : (
				""
			)}
			<Box sx={{ display: "flex", gap: "2%", width: "49%", mt: 2.5 }}>
				<FormControl sx={{ width: "50%" }}>
					<InputLabel className={errors?.salutation != null ? "red" : ""} id="salutation">
						{t("salutation")}
					</InputLabel>
					<Select
						inputProps={{ readOnly: props.readOnly ?? false }}
						labelId="salutation"
						id="select-salutation"
						value={salutationField}
						error={Boolean(errors?.salutation)}
						label={t("salutation")}
						onChange={(event) => {
							setInContact({
								...contact,
								salutation: event.target.value as SalutationType,
							});
							setErrors?.({ ...errors, salutation: "" });
						}}>
						<MenuItem value={SalutationType.Mr}>{t("salutations.mr")}</MenuItem>
						<MenuItem value={SalutationType.Mrs}>{t("salutations.mrs")}</MenuItem>
					</Select>
					{errors?.salutation != null ? (
						<FormHelperText className="red">{errors.salutation as string}</FormHelperText>
					) : null}
				</FormControl>
				<FormControl sx={{ width: "50%" }}>
					<InputLabel id="title">{t("title")}</InputLabel>
					<Select
						inputProps={{ readOnly: props.readOnly ?? false }}
						labelId="title"
						id="select-title"
						value={titleField}
						label={t("title")}
						onChange={(event) => {
							setInContact({ ...contact, title: event.target.value });
							setErrors?.({ ...errors, title: "" });
						}}>
						<MenuItem value="">{t("title.no-title")}</MenuItem>
						<MenuItem value="PhD">PhD</MenuItem>
						<MenuItem value="MSc">MSc</MenuItem>
						<MenuItem value="Dr.">Dr.</MenuItem>
						<MenuItem value="Prof.">Prof.</MenuItem>
					</Select>
				</FormControl>
			</Box>
			<div className="clear" />
			<Box sx={{ display: "flex", gap: "2%", mt: 2.5 }}>
				<BoundTextField
					additionalProps={{ className: "w-50", inputProps: { readOnly: props.readOnly ?? false } }}
					boundedValue={firstNameField}
					label={t("first-name")}
				/>
				<BoundTextField
					additionalProps={{ className: "w-50", inputProps: { readOnly: props.readOnly ?? false } }}
					boundedValue={lastNameField}
					label={t("last-name")}
				/>
			</Box>
			{props.isDialog === true ? (
				""
			) : (
				<Box className="fullCenter">
					<BoundTextField
						additionalProps={{ inputProps: { readOnly: props.readOnly ?? false } }}
						boundedValue={contactNumberField}
						label={t("contact-number")}
					/>
				</Box>
			)}
		</div>
	);
};
